import { ThemeOptions } from '@mui/material';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/700.css';

/*
 * // https://mui.com/material-ui/customization/palette
 * primary
 * - main color of the app, for the header background, etc.
 * - brand color
 * - @todo then why does mui used primary.main for input border color and checkbox background?
 *
 * secondary
 * - accent and distinguish
 * - action buttons, selection controls
 * - links, progress bars
 */

interface ChipPalette {
  label: string;
  infoBackground: string;
  primaryBackground: string;
  secondaryBackground: string;
  greyBackground: string;
  criticalBackground: string;
  criticalOutline: string;
  outline: string;
}

declare module '@mui/material/styles' {
  interface TypeBackground {
    // the signed-in app/content background (behind pages and the header)
    app: string;
  }

  interface Palette {
    subtleGray: string;
    lightGray: string;
    gray: string;
    darkGray: string;
    portal: {
      background: {
        dark: string;
        light: string;
      };
    };
    surface: {
      primary: {
        main: string;
      };
      secondary: {
        main: string;
      };
      info: {
        main: string;
      };
      error: {
        main: string;
      };
      warning: {
        main: string;
      };
      success: {
        main: string;
      };
      disabled: {
        main: string;
      };
      successHighlight: {
        main: string;
      };
      infoHighlight: {
        main: string;
      };
      grey: {
        main: string;
      }
    };
    link: {
      main: string;
      hover: string;
    };
    chip: ChipPalette;
    navigation: {
      background: string;
      text: string;
      hover: string;
      hoverText: string;
      selected: string;
      selectedText: string;
      divider: string;
      footer: string;
      scrollbar: string;
    };
    tooltip: {
      background: string;
    };
    toggle: {
      off: string;
      offHover: string;
      on: string;
      onHover: string;
      offAlternate: string;
    };
  }

  interface PaletteOptions {
    subtleGray: string;
    lightGray: string;
    gray: string;
    darkGray: string;
    portal: {
      background: {
        dark: string;
        light: string;
      };
    };
    surface: {
      primary: {
        main: string;
      };
      secondary: {
        main: string;
      };
      info: {
        main: string;
      };
      error: {
        main: string;
      };
      warning: {
        main: string;
      };
      success: {
        main: string;
      };
      disabled: {
        main: string;
      };
      successHighlight: {
        main: string;
      };
      infoHighlight: {
        main: string;
      };
      grey: {
        main: string;
      }
    };
    link: {
      main: string;
      hover: string;
    };
    chip: ChipPalette;
    navigation: {
      background: string;
      text: string;
      hover: string;
      hoverText: string;
      selected: string;
      selectedText: string;
      divider: string;
      footer: string;
      scrollbar: string;
    };
    tooltip: {
      background: string;
    };
    toggle: {
      off: string;
      offHover: string;
      on: string;
      onHover: string;
      offAlternate: string;
    };
  }
}

/*
 * Everything that is NOT a color lives here and is shared across every color scheme.
 * A new design-system color scheme should only need to supply a new `PaletteOptions`
 * object (see ./surepath and ./f5) — never touch typography or shape.
 */
export const defaultThemeOptions: Omit<ThemeOptions, 'palette'> = {
  typography: {
    fontFamily: 'Inter, Arial',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 450,
    fontWeightBold: 500,
    button: {
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 5,
  },
};
